/**
 * IndexedDB wrapper for trip history and recent destinations.
 * Hand-rolled rather than pulling in a wrapper library - the surface we need is
 * four calls, and the head unit benefits from a smaller bundle.
 */

import type { Place, Trip } from './types';

const DB_NAME = 'wayfarer';
const DB_VERSION = 1;
const STORE_TRIPS = 'trips';
const STORE_RECENTS = 'recents';

let dbPromise: Promise<IDBDatabase> | null = null;

function open(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_TRIPS)) {
        const s = db.createObjectStore(STORE_TRIPS, { keyPath: 'id' });
        s.createIndex('startedAt', 'startedAt');
      }
      if (!db.objectStoreNames.contains(STORE_RECENTS)) {
        db.createObjectStore(STORE_RECENTS, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

function tx<T>(store: string, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest): Promise<T> {
  return open().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const t = db.transaction(store, mode);
        const req = fn(t.objectStore(store));
        req.onsuccess = () => resolve(req.result as T);
        req.onerror = () => reject(req.error);
      }),
  );
}

/* ------------------------------- trips ---------------------------------- */

export async function saveTrip(trip: Trip): Promise<void> {
  try {
    await tx<void>(STORE_TRIPS, 'readwrite', (s) => s.put(trip));
  } catch (err) {
    console.warn('[db] saveTrip failed', err);
  }
}

export async function listTrips(limit = 100): Promise<Trip[]> {
  try {
    const all = await tx<Trip[]>(STORE_TRIPS, 'readonly', (s) => s.getAll());
    return all.sort((a, b) => b.startedAt - a.startedAt).slice(0, limit);
  } catch {
    return [];
  }
}

export async function deleteTrip(id: string): Promise<void> {
  try {
    await tx<void>(STORE_TRIPS, 'readwrite', (s) => s.delete(id));
  } catch (err) {
    console.warn('[db] deleteTrip failed', err);
  }
}

export async function clearTrips(): Promise<void> {
  try {
    await tx<void>(STORE_TRIPS, 'readwrite', (s) => s.clear());
  } catch (err) {
    console.warn('[db] clearTrips failed', err);
  }
}

/* ------------------------------ recents --------------------------------- */

interface RecentRecord extends Place {
  usedAt: number;
}

export async function addRecent(place: Place): Promise<void> {
  try {
    await tx<void>(STORE_RECENTS, 'readwrite', (s) =>
      s.put({ ...place, usedAt: Date.now() } satisfies RecentRecord),
    );
  } catch (err) {
    console.warn('[db] addRecent failed', err);
  }
}

export async function listRecents(limit = 12): Promise<Place[]> {
  try {
    const all = await tx<RecentRecord[]>(STORE_RECENTS, 'readonly', (s) => s.getAll());
    return all.sort((a, b) => b.usedAt - a.usedAt).slice(0, limit);
  } catch {
    return [];
  }
}
